/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, throwError, timeout } from 'rxjs';

import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';

import { SnackBarService } from './components/snack-bar/snack-bar.service';
import { ErrorDialogComponent } from '../../lib/components/dialog/error-dialog/error-dialog.component';
import { ENVIRONMENT } from './injection-tokens';
import { Logger } from './logger/logger';

import { VitamUITimeoutError } from './models/http-interceptor/vitamui-timeout-error';
import { StartupService } from './startup.service';
import { SKIP_ERROR_NOTIFICATION } from './utils/http-header.util';
import { VitamuiHttpHeaders } from './vitamui-http-headers.enum';
import { ErrorsDetailsDialogComponent } from '../../lib/components/dialog/errors-details-dialog/errors-details-dialog.component';

const URLS_INCREASED_TIMEOUT = ['file', 'download', 'export'];
// @ts-ignore
const HTTP_STATUS_CODE_BAD_REQUEST = 400;
const HTTP_STATUS_CODE_UNAUTHORIZED = 401;
const HTTP_STATUS_CODE_INTERNAL_SERVER_ERROR = 500;
const DEFAULT_API_TIMEOUT = 50000;
// @ts-ignore
const CLIENT_ERROR_START = 400;
// @ts-ignore
const SERVER_ERROR_START = 500;

// @ts-ignore
const NOTIFICATION_DELAY_MS = 20000;
const DEFAULT_DOWNLOAD_UPLOAD_API_TIMEOUT = 1000 * 60 * 60 * 5;
const DEFAULT_SERVER_ERROR_MESSAGE = 'EXCEPTIONS.HTTP_INTERCEPTOR.SERVER_ERROR_START';
const ERROR_NOTIFICATION_MESSAGE_BY_HTTP_STATUS: Map<number, string> = new Map([
  [403, 'EXCEPTIONS.HTTP_INTERCEPTOR.HTTP_STATUS_CODE_FORBIDDEN'],
  [400, 'EXCEPTIONS.HTTP_INTERCEPTOR.HTTP_STATUS_CODE_BAD_REQUEST'],
  [404, 'EXCEPTIONS.HTTP_INTERCEPTOR.HTTP_STATUS_CODE_NOT_FOUND'],
  [503, 'EXCEPTIONS.HTTP_INTERCEPTOR.HTTP_STATUS_CODE_SERVICE_UNAVAILABLE'],
  [504, 'EXCEPTIONS.HTTP_INTERCEPTOR.HTTP_STATUS_CODE_GATEWAY_TIMEOUT'],
  [417, 'EXCEPTIONS.HTTP_INTERCEPTOR.HTTP_STATUS_CODE_EXPECTATION_FAILED'],
  [412, 'EXCEPTIONS.HTTP_INTERCEPTOR.HTTP_STATUS_PRECONDITION_FAILED_EXCEPTION'],
  [408, 'EXCEPTIONS.HTTP_INTERCEPTOR.HTTP_STATUS_CODE_REQUEST_TIMEOUT'],
]);

const CONTEXT_COLLECT = 'Collect';

@Injectable()
export class VitamUIHttpInterceptor implements HttpInterceptor {
  private logger = inject(Logger);
  private matDialog = inject(MatDialog);
  private startupService = inject(StartupService);
  private authService = inject(AuthService);
  private injector = inject(Injector);
  private environment = inject(ENVIRONMENT);

  private errorDialog: MatDialogRef<ErrorDialogComponent>;
  private errorsDetailsDialog: MatDialogRef<ErrorsDetailsDialogComponent>;
  private snackBarService: SnackBarService;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.initSnackBarService();

    let tenantIdentifier = request.headers.get(VitamuiHttpHeaders.X_TENANT_ID);
    if ((!tenantIdentifier || tenantIdentifier === '') && this.authService.user) {
      // TODO: change to tenant-selection.service
      tenantIdentifier = this.startupService.getTenantIdentifier();
    }
    let requestId = request.headers.get(VitamuiHttpHeaders.X_REQUEST_ID);
    if (!requestId) {
      requestId = '' + Math.floor(Math.random() * 10 ** 15);
    }
    let applicationId = request.headers.get(VitamuiHttpHeaders.X_APPLICATION_ID);
    if (!applicationId) {
      applicationId = this.startupService.CURRENT_APP_ID;
    }

    const headerTimeout = Number(request.headers.get(VitamuiHttpHeaders.X_API_TIMEOUT));
    const isUpload = request.headers.get('Content-Type') === 'application/octet-stream';
    const isBigDownload = URLS_INCREASED_TIMEOUT.some((url) => request.url.includes(url));
    const apiTimeout =
      headerTimeout && !isNaN(headerTimeout)
        ? headerTimeout
        : isUpload || isBigDownload
          ? DEFAULT_DOWNLOAD_UPLOAD_API_TIMEOUT
          : this.environment?.apiTimeout
            ? this.environment.apiTimeout
            : DEFAULT_API_TIMEOUT;

    const reqWithCredentials = request.clone({
      withCredentials: true,
      headers: request.headers.delete(VitamuiHttpHeaders.X_BY_PASSED_ERROR),
      setHeaders: {
        [VitamuiHttpHeaders.X_REQUEST_ID]: requestId,
        [VitamuiHttpHeaders.X_REQUEST_TIMESTAMP]: Math.floor(Date.now() / 1000).toString(),
        [VitamuiHttpHeaders.X_REQUESTED_WITH]: 'XMLHttpRequest',
        [VitamuiHttpHeaders.X_TENANT_ID]: tenantIdentifier ? tenantIdentifier.toString() : '-1',
        [VitamuiHttpHeaders.X_APPLICATION_ID]: applicationId,
      },
    });

    let errorToByPass: number = null;
    if (request.headers.has(VitamuiHttpHeaders.X_BY_PASSED_ERROR)) {
      errorToByPass = +request.headers.get(VitamuiHttpHeaders.X_BY_PASSED_ERROR);
    }

    return next.handle(reqWithCredentials).pipe(
      timeout({ each: apiTimeout, with: () => throwError(() => new VitamUITimeoutError()) }),
      catchError((response) => {
        if (response instanceof HttpErrorResponse && response.status !== errorToByPass) {
          this.logger.log(this, 'Processing http error', response);
          if (response.status === HTTP_STATUS_CODE_UNAUTHORIZED) {
            if (!this.environment.production && request && request.url.endsWith('/security')) {
              // MDI : hack for dev purposes, with the first connection, we redirect the user to login
              this.authService.redirectToLoginPage();
            } else {
              // connection was lost, we need to logout the user
              this.authService.logout();
            }
          } else {
            this.errorNotification(response, request);
          }
        }
        this.logger.log(this, 'Request error', response);
        return throwError(response);
      }),
    );
  }

  private initSnackBarService(): void {
    if (!this.snackBarService) {
      try {
        this.snackBarService = this.injector.get(SnackBarService);
      } catch (error) {}
    }
  }

  private errorNotification(response: HttpErrorResponse, request: HttpRequest<any>): void {
    if (!request.headers.has(SKIP_ERROR_NOTIFICATION)) {
      response.error.context === CONTEXT_COLLECT && response.error.httpCode === HttpStatusCode.BadRequest
        ? this.displayErrorsDetailsDialog(response.error)
        : response.status === HTTP_STATUS_CODE_INTERNAL_SERVER_ERROR
          ? this.displayErrorDialog()
          : this.displaySnackBar(response);
    }
  }

  private displaySnackBar(response: HttpErrorResponse): void {
    const errorMsg = ERROR_NOTIFICATION_MESSAGE_BY_HTTP_STATUS.has(response.status)
      ? ERROR_NOTIFICATION_MESSAGE_BY_HTTP_STATUS.get(response.status)
      : DEFAULT_SERVER_ERROR_MESSAGE;
    this.snackBarService.open({ message: errorMsg });
  }

  private displayErrorDialog(): void {
    if (!this.errorDialog) {
      this.errorDialog = this.matDialog.open(ErrorDialogComponent);
      this.errorDialog.afterClosed().subscribe(() => (this.errorDialog = null));
    }
  }

  private displayErrorsDetailsDialog(error: any) {
    if (!this.errorsDetailsDialog) {
      this.errorsDetailsDialog = this.matDialog.open(ErrorsDetailsDialogComponent, {
        data: error,
      });
      this.errorsDetailsDialog.afterClosed().subscribe(() => (this.errorsDetailsDialog = null));
    }
  }
}
